<template>
  <div class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 z-50 animate-modalFade">
    <div class="w-full max-w-4xl bg-gray-800 text-gray-300 rounded-lg p-5 flex flex-col gap-4 relative shadow-lg border border-gray-700 animate-scaleIn">
      <!-- Header -->
      <div class="flex justify-between items-center border-b border-gray-700 pb-3">
        <h2 class="text-xl font-semibold text-indigo-300">ไอเทมที่สวมใส่</h2>
        <button
          v-if="!isUnequipping"
          @click="$emit('closeEquippedItems')"
          class="text-gray-400 hover:text-indigo-300 transition-colors duration-200"
        >
          <i class="fas fa-times text-lg"></i>
        </button>
      </div>
      <!-- Tab Navigation -->
      <div class="flex border-b border-gray-700">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          class="px-4 py-2 text-sm font-medium transition-colors duration-200 flex items-center gap-2"
          :class="activeTab === tab.id
            ? 'text-indigo-200 border-b-2 border-indigo-700'
            : 'text-gray-400 hover:text-indigo-300'"
        >
          <i :class="tab.icon" class="text-lg"></i>
          <span v-if="!isMobile" class="hidden md:inline">{{ tab.name }}</span>
        </button>
      </div>
      <!-- Content -->
      <div v-if="loading" class="flex items-center justify-center py-6">
        <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-red-600"></div>
      </div>
      <div v-else-if="error" class="text-center text-red-300 py-6">
        <p>{{ error }}</p>
      </div>
      <div v-else class="overflow-y-auto max-h-96">
        <!-- Tab: Equipment -->
        <div v-if="activeTab === 'equipment'" class="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-3">
          <div
            v-for="type in allItemTypes"
            :key="type"
            class="bg-gray-800 rounded-md p-2 flex flex-col items-center border border-gray-700 transition-all duration-200 relative"
            :class="{
              'hover:border-indigo-600 cursor-pointer': equippedByType[type],
              'opacity-50': !equippedByType[type],
              'border-2 border-indigo-600': selectedItem && selectedItem?.type === type
            }"
            @click="equippedByType[type] ? selectItem(equippedByType[type], 'equipped') : null"
          >
            <div v-if="equippedByType[type]" class="relative">
              <img
                :src="equippedByType[type]?.img || 'default-item.png'"
                alt="Item"
                class="w-14 h-14 rounded-md object-cover border-2 mb-1 mx-auto"
                :class="[
                  equippedByType[type]?.rarityColors?.borderColor || 'border-gray-600',
                  equippedByType[type]?.rarityColors?.bgColor || 'bg-gray-800'
                ]"
                @error="handleImageError"
              />
              <div class="absolute bottom-0 left-1/2 -translate-x-1/2 bg-gray-700 bg-opacity-80 border border-gray-600 px-1 py-0.5 rounded flex items-center gap-1">
                <span class="text-xs font-semibold">{{ equippedByType[type]?.rarity || 0 }}</span>
                <i class="fas fa-star text-yellow-400 text-xs"></i>
              </div>
              <div
                v-if="isPunishmentProtected(type)"
                class="absolute -top-1 -right-1 bg-emerald-600 text-white text-2xs px-1 py-0.5 rounded-full border border-emerald-400 shadow-sm z-10"
                title="ไอเทมประเภทนี้จะไม่สูญหายเมื่อผู้เล่นตาย"
              >
                <i class="fas fa-shield-alt"></i>
              </div>
            </div>
            <i v-else :class="typeIcons[type] || 'fas fa-ban'" class="text-3xl text-indigo-200 mb-1"></i>
            <p
              v-if="equippedByType[type]"
              class="text-xs text-center truncate"
              :class="equippedByType[type]?.rarityColors?.textColor || 'text-gray-300'"
              :title="equippedByType[type]?.name"
            >
              {{ equippedByType[type]?.name || 'ไม่ระบุ' }}<span v-if="equippedByType[type]?.level">+{{ equippedByType[type]?.level }}</span>
              <i v-if="equippedByType[type]?.transfer" class="fa-solid fa-arrow-right-arrow-left text-green-400 text-xs"></i>
            </p>
            <p class="text-xs text-gray-300">ประเภท: {{ type }}</p>
            <p v-if="!equippedByType[type]" class="text-xs text-gray-300">ยังไม่ได้สวมใส่</p>
          </div>
        </div>
        <!-- Tab: Mechanical Organs -->
        <div v-if="activeTab === 'mechanical'" class="flex flex-col gap-4">
          <!-- Group: Skill 1 -->
          <div class="rounded-md border border-gray-700 overflow-hidden">
            <div class="sticky top-0 bg-gray-900/95 backdrop-filter backdrop-blur px-4 py-3 border-b border-gray-700 flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="chip chip-green">ทักษะ</span>
                <h3 class="text-sm font-semibold text-indigo-200">หนังสือวิธีติดตั้งอวัยวะจักรกล</h3>
              </div>
              <span class="text-2xs text-gray-400">พื้นฐาน</span>
            </div>
            <div class="p-3 grid grid-cols-4 md:grid-cols-6 lg:grid-cols-7 gap-3">
              <div
                v-for="name in mechanicalNamesSkill1"
                :key="name"
                class="bg-gray-800 rounded-md p-2 border border-gray-700 transition-all duration-200 relative flex flex-col items-center"
                :class="{
                  'hover:border-indigo-600 cursor-pointer': mechanicalByName[name],
                  'opacity-60': !mechanicalByName[name],
                  'border-2 border-indigo-600': selectedItem && selectedItem?.name === name
                }"
                @click="mechanicalByName[name] ? selectItem(mechanicalByName[name], 'mechanical') : null"
              >
                <div v-if="mechanicalByName[name]" class="relative w-full flex justify-center">
                  <img
                    :src="mechanicalByName[name]?.img || 'default-item.png'"
                    alt="Mechanical Organ"
                    class="w-14 h-14 rounded-md object-cover border-2 mb-1 mx-auto"
                    :class="[
                      mechanicalByName[name]?.rarityColors?.borderColor || 'border-gray-600',
                      mechanicalByName[name]?.rarityColors?.bgColor || 'bg-gray-800'
                    ]"
                    @error="handleImageError"
                  />
                  <div class="absolute bottom-0 left-1/2 -translate-x-1/2 bg-gray-700 bg-opacity-80 border border-gray-600 px-1 py-0.5 rounded flex items-center gap-1">
                    <span class="text-xs font-semibold">{{ mechanicalByName[name]?.rarity || 0 }}</span>
                    <i class="fas fa-star text-yellow-400 text-xs"></i>
                  </div>
                </div>
                <div v-else class="h-[3.5rem] w-full flex items-center justify-center border border-dashed border-gray-700 rounded mb-1">
                  <i class="fas fa-cogs text-2xl text-indigo-200"></i>
                </div>
                <p
                  v-if="mechanicalByName[name]"
                  class="text-2xs text-center truncate"
                  :class="mechanicalByName[name]?.rarityColors?.textColor || 'text-gray-300'"
                  :title="mechanicalByName[name]?.name"
                >
                  {{ mechanicalByName[name]?.name || 'ไม่ระบุ' }}
                  <i v-if="mechanicalByName[name]?.transfer" class="fa-solid fa-arrow-right-arrow-left text-green-400 text-xs"></i>
                </p>
                <p class="text-2xs text-gray-400 text-center truncate" :title="name">{{ name }}</p>
              </div>
            </div>
          </div>
          <!-- Group: Skill 2 -->
          <div class="rounded-md border border-gray-700 overflow-hidden">
            <div class="sticky top-0 bg-gray-900/95 backdrop-filter backdrop-blur px-4 py-3 border-b border-gray-700 flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="chip chip-green">ทักษะ</span>
                <h3 class="text-sm font-semibold text-indigo-200">หนังสือวิธีติดตั้งอวัยวะจักรกล2</h3>
              </div>
              <span class="text-2xs text-gray-400">ขั้นสูง</span>
            </div>
            <div class="p-3 grid grid-cols-4 md:grid-cols-6 lg:grid-cols-7 gap-3">
              <div
                v-for="name in mechanicalNamesSkill2"
                :key="name"
                class="bg-gray-800 rounded-md p-2 border border-gray-700 transition-all duration-200 relative flex flex-col items-center"
                :class="{
                  'hover:border-indigo-600 cursor-pointer': mechanicalByName[name],
                  'opacity-60': !mechanicalByName[name],
                  'border-2 border-indigo-600': selectedItem && selectedItem?.name === name
                }"
                @click="mechanicalByName[name] ? selectItem(mechanicalByName[name], 'mechanical') : null"
              >
                <div v-if="mechanicalByName[name]" class="relative w-full flex justify-center">
                  <img
                    :src="mechanicalByName[name]?.img || 'default-item.png'"
                    alt="Mechanical Organ"
                    class="w-14 h-14 rounded-md object-cover border-2 mb-1 mx-auto"
                    :class="[
                      mechanicalByName[name]?.rarityColors?.borderColor || 'border-gray-600',
                      mechanicalByName[name]?.rarityColors?.bgColor || 'bg-gray-800'
                    ]"
                    @error="handleImageError"
                  />
                  <div class="absolute bottom-0 left-1/2 -translate-x-1/2 bg-gray-700 bg-opacity-80 border border-gray-600 px-1 py-0.5 rounded flex items-center gap-1">
                    <span class="text-xs font-semibold">{{ mechanicalByName[name]?.rarity || 0 }}</span>
                    <i class="fas fa-star text-yellow-400 text-xs"></i>
                  </div>
                </div>
                <div v-else class="h-[3.5rem] w-full flex items-center justify-center border border-dashed border-gray-700 rounded mb-1">
                  <i class="fas fa-cogs text-2xl text-indigo-200"></i>
                </div>
                <p
                  v-if="mechanicalByName[name]"
                  class="text-2xs text-center truncate"
                  :class="mechanicalByName[name]?.rarityColors?.textColor || 'text-gray-300'"
                  :title="mechanicalByName[name]?.name"
                >
                  {{ mechanicalByName[name]?.name || 'ไม่ระบุ' }}
                  <i v-if="mechanicalByName[name]?.transfer" class="fa-solid fa-arrow-right-arrow-left text-green-400 text-xs"></i>
                </p>
                <p class="text-2xs text-gray-400 text-center truncate" :title="name">{{ name }}</p>
              </div>
            </div>
          </div>
          <!-- Group: Skill 3 -->
          <div class="rounded-md border border-gray-700 overflow-hidden">
            <div class="sticky top-0 bg-gray-900/95 backdrop-filter backdrop-blur px-4 py-3 border-b border-gray-700 flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="chip chip-green">ทักษะ</span>
                <h3 class="text-sm font-semibold text-indigo-200">หนังสือวิธีติดตั้งอวัยวะจักรกล3</h3>
              </div>
              <span class="text-2xs text-gray-400">ชั้นเชิงล้ำ</span>
            </div>
            <div class="p-3 grid grid-cols-4 md:grid-cols-6 lg:grid-cols-7 gap-3">
              <div
                v-for="name in mechanicalNamesSkill3"
                :key="name"
                class="bg-gray-800 rounded-md p-2 border border-gray-700 transition-all duration-200 relative flex flex-col items-center"
                :class="{
                  'hover:border-indigo-600 cursor-pointer': mechanicalByName[name],
                  'opacity-60': !mechanicalByName[name],
                  'border-2 border-indigo-600': selectedItem && selectedItem?.name === name
                }"
                @click="mechanicalByName[name] ? selectItem(mechanicalByName[name], 'mechanical') : null"
              >
                <div v-if="mechanicalByName[name]" class="relative w-full flex justify-center">
                  <img
                    :src="mechanicalByName[name]?.img || 'default-item.png'"
                    alt="Mechanical Organ"
                    class="w-14 h-14 rounded-md object-cover border-2 mb-1 mx-auto"
                    :class="[
                      mechanicalByName[name]?.rarityColors?.borderColor || 'border-gray-600',
                      mechanicalByName[name]?.rarityColors?.bgColor || 'bg-gray-800'
                    ]"
                    @error="handleImageError"
                  />
                  <div class="absolute bottom-0 left-1/2 -translate-x-1/2 bg-gray-700 bg-opacity-80 border border-gray-600 px-1 py-0.5 rounded flex items-center gap-1">
                    <span class="text-xs font-semibold">{{ mechanicalByName[name]?.rarity || 0 }}</span>
                    <i class="fas fa-star text-yellow-400 text-xs"></i>
                  </div>
                </div>
                <div v-else class="h-[3.5rem] w-full flex items-center justify-center border border-dashed border-gray-700 rounded mb-1">
                  <i class="fas fa-cogs text-2xl text-indigo-200"></i>
                </div>
                <p
                  v-if="mechanicalByName[name]"
                  class="text-2xs text-center truncate"
                  :class="mechanicalByName[name]?.rarityColors?.textColor || 'text-gray-300'"
                  :title="mechanicalByName[name]?.name"
                >
                  {{ mechanicalByName[name]?.name || 'ไม่ระบุ' }}
                  <i v-if="mechanicalByName[name]?.transfer" class="fa-solid fa-arrow-right-arrow-left text-green-400 text-xs"></i>
                </p>
                <p class="text-2xs text-gray-400 text-center truncate" :title="name">{{ name }}</p>
              </div>
            </div>
          </div>
        </div>
        <!-- Tab: Vehicle -->
        <div v-if="activeTab === 'vehicle'" class="grid grid-cols-1 gap-3">
          <div
            v-if="equippedVehicle"
            class="bg-gray-800 rounded-md p-2 flex flex-col items-center border border-gray-700 transition-all duration-200 hover:border-indigo-600 cursor-pointer relative"
            @click="selectItem(equippedVehicle, 'vehicle')"
            :class="{ 'border-2 border-indigo-600': selectedItem && selectedItem?.id === equippedVehicle?.id }"
          >
            <div class="relative">
              <img
                :src="equippedVehicle?.img || 'default-item.png'"
                alt="Vehicle"
                class="w-14 h-14 rounded-md object-cover border-2 mb-1 mx-auto"
                :class="[
                  equippedVehicle?.rarityColors?.borderColor || 'border-gray-600',
                  equippedVehicle?.rarityColors?.bgColor || 'bg-gray-800'
                ]"
                @error="handleImageError"
              />
              <div class="absolute bottom-0 left-1/2 -translate-x-1/2 bg-gray-700 bg-opacity-80 border border-gray-600 px-1 py-0.5 rounded flex items-center gap-1">
                <span class="text-xs font-semibold">{{ equippedVehicle?.rarity || 'ทั่วไป' }}</span>
                <i class="fas fa-star text-yellow-400 text-xs"></i>
              </div>
            </div>
            <p class="text-xs text-center truncate" :class="equippedVehicle?.rarityColors?.textColor || 'text-gray-300'" :title="equippedVehicle?.name">
              {{ equippedVehicle?.name || 'ไม่ระบุ' }}
              <i v-if="equippedVehicle?.transfer" class="fa-solid fa-arrow-right-arrow-left text-green-400 text-xs"></i>
            </p>
            <p class="text-xs text-gray-300">ประเภท: {{ equippedVehicle?.type || 'พาหนะ' }}</p>
            <p class="text-xs text-gray-300">
  น้ำมัน: {{ getVehicleFuel(equippedVehicle) }} / {{ getVehicleMaxFuel(equippedVehicle) }}
</p>
          </div>
          <div v-else class="bg-gray-800 rounded-md p-2 flex flex-col items-center border border-gray-700 opacity-50">
            <i class="fas fa-motorcycle text-3xl text-indigo-200 mb-1"></i>
            <p class="text-xs text-gray-300">พาหนะ</p>
            <p class="text-xs text-gray-300">ยังไม่ได้ติดตั้ง</p>
          </div>
        </div>
        <!-- Tab: Ghosts -->
        <div v-if="activeTab === 'ghosts'" class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          <!-- Equipped ghosts -->
          <div
            v-for="(ghost, index) in equippedGhosts || []"
            :key="ghost?.id || index"
            class="bg-gray-800 rounded-md p-2 flex flex-col items-center border border-gray-700 transition-all duration-200 hover:border-indigo-600 cursor-pointer relative"
            :class="{ 'border-2 border-indigo-600': selectedItem && selectedItem?.id === ghost?.id }"
            @click="ghost ? selectItem(ghost, 'ghost') : null"
          >
            <div class="relative">
              <img
                :src="ghost?.img || 'default-ghost.png'"
                alt="Ghost"
                class="w-14 h-14 rounded-md object-cover border-2 mb-1 mx-auto"
                :class="[
                  ghost?.rarityColors?.borderColor || 'border-gray-600',
                  ghost?.rarityColors?.bgColor || 'bg-gray-800'
                ]"
                @error="handleImageError"
              />
              <div
                class="absolute bottom-0 left-1/2 -translate-x-1/2 bg-gray-700 bg-opacity-80 border border-gray-600 px-1 py-0.5 rounded flex items-center gap-1"
              >
                <span class="text-xs font-semibold">{{ ghost?.rarity || 'ทั่วไป' }}</span>
                <i class="fas fa-star text-yellow-400 text-xs"></i>
              </div>
            </div>
            <p
              class="text-xs text-center truncate"
              :class="ghost?.rarityColors?.textColor || 'text-gray-300'"
              :title="ghost?.name"
            >
              {{ ghost?.name || 'ไม่ระบุ' }}
              <i v-if="ghost?.transfer" class="fa-solid fa-arrow-right-arrow-left text-green-400 text-xs"></i>
            </p>
            <p class="text-xs text-gray-300">ประเภท: {{ ghost?.subtype || 'วิญญาณ' }}</p>
          </div>

          <!-- Placeholders for each subtype (hidden when that subtype is already equipped) -->
          <div
            v-for="subtype in ghostSubtypes"
            :key="`placeholder-${subtype}`"
            class="bg-gray-800 rounded-md p-2 flex flex-col items-center border border-gray-700 opacity-50"
            :class="{ 'hidden': isGhostSubtypeEquipped(subtype) }"
          >
            <i class="fas fa-ghost text-3xl text-indigo-200 mb-1"></i>
            <p class="text-xs text-gray-300">{{ subtype }}</p>
            <p class="text-xs text-gray-300">ยังไม่ได้อัญเชิญ</p>
          </div>
        </div>

        <!-- Tab: Set Bonuses -->
        <div v-if="activeTab === 'setBonuses'" class="">
          <div v-if="setProgress.length" class="space-y-4">
            <div
              v-for="setInfo in setProgress"
              :key="setInfo.setName"
              class="rounded-md border border-gray-700 hover:border-indigo-600 transition-colors duration-200"
            >
              <!-- Header -->
              <div class="px-4 py-3 flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <span class="chip chip-green">เซ็ต</span>
                  <h4 class="text-sm font-semibold text-indigo-200">{{ setInfo.setName }}</h4>
                </div>
                <div class="flex items-center gap-2">
                  <span class="chip chip-green">
                    สวมใส่ {{ setInfo.equippedCount }} / {{ setInfo.totalItems }}
                  </span>
                </div>
              </div>
              <!-- Progress bar -->
              <div class="px-4 pb-3">
                <div class="w-full bg-gray-800 rounded-full h-2">
                  <div
                    class="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                    :style="{ width: `${(setInfo.equippedCount / setInfo.totalItems) * 100}%` }"
                  ></div>
                </div>
              </div>
              <!-- Items grid -->
              <div class="px-4 pb-3">
                <p class="text-xs text-gray-400 mb-1">รายการไอเทมในเซ็ต:</p>
                <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5">
                  <div
                    v-for="name in setConfigs[setInfo.setName].items"
                    :key="name"
                    class="flex items-center justify-between text-xs px-2 py-1 rounded border"
                    :class="equippedNamesSet.has(name)
                      ? 'border-green-700 bg-green-900 bg-opacity-30 text-green-200'
                      : 'border-gray-700 bg-gray-800 text-gray-300 opacity-80'"
                  >
                    <span class="truncate" :title="name">{{ name }}</span>
                    <i :class="equippedNamesSet.has(name) ? 'fas fa-check-circle' : 'far fa-circle'" class="ml-2"></i>
                  </div>
                </div>
              </div>
              <!-- Current bonus -->
              <div class="px-4 pb-3">
                <p class="text-xs text-gray-400 mb-1">โบนัสปัจจุบัน:</p>
                <div class="rounded-md border border-gray-700 bg-gray-800">
                  <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 p-3">
                    <div v-for="stat in statOrder" :key="stat + '-current'" class="stat-pill">
                      <i :class="statMeta[stat]?.icon || 'fas fa-plus-circle'" class="text-xs"></i>
                      <span class="text-2xs text-gray-400">{{ statMeta[stat]?.label || stat }}</span>
                      <span class="font-semibold" :class="getStat(setInfo.currentBonus, stat) ? 'text-green-300' : 'text-gray-500'">
                        {{ formatBonus(getStat(setInfo.currentBonus, stat)) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Next bonuses -->
              <div class="px-4 pb-4">
                <p class="text-xs text-gray-400 mb-2">โบนัสต่อไป:</p>
                <div v-if="setInfo.nextBonuses.length" class="space-y-2">
                  <div
                    v-for="(next, idx) in setInfo.nextBonuses"
                    :key="setInfo.setName + '-next-' + idx"
                    class="rounded-md border border-yellow-700 bg-yellow-900 bg-opacity-10 p-3"
                  >
                    <div class="flex items-center justify-between mb-2">
                      <div class="flex items-center gap-2">
                        <i class="fas fa-level-up-alt text-yellow-300 text-xs"></i>
                        <span class="text-xs text-yellow-300">
                          ต้องใส่อีก <b>{{ Math.max(next.required - setInfo.equippedCount, 0) }}</b> ชิ้น (รวม {{ next.required }})
                        </span>
                      </div>
                      <span class="chip chip-amber">ขั้น {{ next.required }}</span>
                    </div>
                    <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                      <div v-for="stat in statOrder" :key="stat + '-next-' + idx" class="stat-pill stat-pill--next">
                        <i :class="statMeta[stat]?.icon || 'fas fa-plus-circle'" class="text-xs"></i>
                        <span class="text-2xs text-gray-400">{{ statMeta[stat]?.label || stat }}</span>
                        <span class="font-semibold" :class="getStat(next.bonus, stat) ? 'text-yellow-200' : 'text-gray-500'">
                          {{ formatBonus(getStat(next.bonus, stat)) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="text-xs text-gray-500 italic">ไม่มีขั้นถัดไป</div>
              </div>
            </div>
          </div>
          <div v-else class="text-center text-gray-400">
            <p>ยังไม่มีการสวมใส่ไอเทมจากเซ็ตใด ๆ</p>
          </div>
        </div>
      </div>
      <!-- Action Buttons -->
      <div v-if="selectedItem" class="flex justify-center gap-3 flex-wrap border-t border-gray-700 pt-3">
        <button
          v-if="!confirmUnequip && selectedItemSource === 'equipped'"
          @click="confirmUnequip = true"
          class="bg-yellow-600 hover:bg-yellow-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-yellow-700"
        >
          <i class="fas fa-undo mr-1 text-xs"></i> ถอดไอเทม
        </button>
        <button
          v-if="confirmUnequip && selectedItemSource === 'equipped'"
          @click="unequipItem"
          class="bg-yellow-600 hover:bg-yellow-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-yellow-700"
          :disabled="isUnequipping"
        >
          <span v-if="!isUnequipping"><i class="fas fa-check mr-1 text-xs"></i> ยืนยันถอดไอเทม</span>
          <div v-else class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
        </button>
        <button
          v-if="!confirmUnequipVehicle && selectedItemSource === 'vehicle'"
          @click="confirmUnequipVehicle = true"
          class="bg-yellow-600 hover:bg-yellow-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-yellow-700"
        >
          <i class="fas fa-undo mr-1 text-xs"></i> ถอดพาหนะ
        </button>
        <button
          v-if="confirmUnequipVehicle && selectedItemSource === 'vehicle'"
          @click="unequipVehicle"
          class="bg-yellow-600 hover:bg-yellow-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-yellow-700"
          :disabled="isUnequipping"
        >
          <span v-if="!isUnequipping"><i class="fas fa-check mr-1 text-xs"></i> ยืนยันถอดพาหนะ</span>
          <div v-else class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
        </button>
        <button
          v-if="selectedItemSource === 'vehicle'"
          @click="showVehicleFuelModal = true"
          class="bg-blue-600 hover:bg-blue-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-blue-700"
        >
          <i class="fas fa-gas-pump mr-1 text-xs"></i> เติมน้ำมัน
        </button>
        <button
          v-if="!confirmUnequipMechanical && selectedItemSource === 'mechanical'"
          @click="confirmUnequipMechanical = true"
          class="bg-yellow-600 hover:bg-yellow-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-yellow-700"
        >
          <i class="fas fa-undo mr-1 text-xs"></i> ถอนการติดตั้งอวัยวะจักรกล
        </button>
        <button
          v-if="confirmUnequipMechanical && selectedItemSource === 'mechanical'"
          @click="unequipMechanicalOrgan"
          class="bg-yellow-600 hover:bg-yellow-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-yellow-700"
          :disabled="isUnequipping"
        >
          <span v-if="!isUnequipping"><i class="fas fa-check mr-1 text-xs"></i> ยืนยันถอนการติดตั้งอวัยวะจักรกล</span>
          <div v-else class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
        </button>
        <button
          v-if="!confirmUnsummon && selectedItemSource === 'ghost'"
          @click="confirmUnsummon = true"
          class="bg-yellow-600 hover:bg-yellow-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-yellow-700"
        >
          <i class="fas fa-undo mr-1 text-xs"></i> ถอนการอัญเชิญวิญญาณ
        </button>
        <button
          v-if="confirmUnsummon && selectedItemSource === 'ghost'"
          @click="unsummonGhost"
          class="bg-yellow-600 hover:bg-yellow-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-yellow-700"
          :disabled="isUnequipping"
        >
          <span v-if="!isUnequipping"><i class="fas fa-check mr-1 text-xs"></i> ยืนยันถอนการอัญเชิญ</span>
          <div v-else class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
        </button>
        <button
          @click="openDetailModal(selectedItem)"
          class="bg-indigo-600 hover:bg-indigo-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-indigo-700"
        >
          <i class="fas fa-info-circle mr-1 text-xs"></i> ดูข้อมูลไอเทม
        </button>
      </div>
      <!-- Footer -->
      <div class="flex justify-end">
        <button
          @click="$emit('closeEquippedItems')"
          class="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-md w-full font-semibold transition-all duration-200 border border-gray-600 text-sm group"
        >
          <i class="fas fa-times mr-1 text-sm group-hover:scale-110"></i> ปิด
        </button>
      </div>
      <!-- Modals -->
      <DetailItemModal
        v-if="showDetailModal"
        :itemId="selectedItem.id"
        :item="selectedItem"
        @close="showDetailModal = false"
      />
      <VehicleFuelModal
        v-if="showVehicleFuelModal"
        :itemId="selectedItem?.id"
        :item="selectedItem"
        :inventoryItems="inventoryItems"
        @close="showVehicleFuelModal = false"
        @vehicle-updated="handleVehicleUpdated"
      />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { useInventoryStore } from '@/composables/useInventoryStore';
import { usePlayerStore } from '@/composables/usePlayerStore';
import DetailItemModal from '@/components/DetailItemModal.vue';
import VehicleFuelModal from '@/components/VehicleFuelModal.vue';
import gameItemsData from '@/data/gameItems.json';

export default {
  name: 'EquippedItemsModal',
  emits: ['closeEquippedItems'],
  components: {
    DetailItemModal,
    VehicleFuelModal
  },
  setup(props, { emit }) {
    const { user } = getUser();
    const playerStore = usePlayerStore();
    const activeTab = ref('equipment');
    const equippedItems = ref([]);
    const equippedVehicle = ref(null);
    const equippedMechanicalOrgans = ref([]);
    const equippedGhosts = ref([]);
    const itemStats = ref({ stamina: 0, str: 0, int: 0, agi: 0, luck: 0, maxHp: 0, atk: 0, evasion: 0, critical: 0, defense: 0 });
    const ghostStats = ref({ stamina: 0, str: 0, int: 0, agi: 0, luck: 0, maxHp: 0, atk: 0, evasion: 0, critical: 0, defense: 0 });
    const setBonuses = ref({});
    const defensePoints = ref(0);
    const equippedItemTypes = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const selectedItem = ref(null);
    const selectedItemSource = ref(null);
    const confirmUnequip = ref(false);
    const confirmUnequipVehicle = ref(false);
    const confirmUnequipMechanical = ref(false);
    const confirmUnsummon = ref(false);
    const isUnequipping = ref(false); // Added to fix ReferenceError
    const showDetailModal = ref(false);
    const showVehicleFuelModal = ref(false);
    const inventoryItems = ref([]);
    const isMobile = ref(window.innerWidth < 768);

    const tabs = ref([
      { id: 'equipment', name: 'อุปกรณ์', icon: 'fas fa-shirt' },
      { id: 'mechanical', name: 'อวัยวะจักรกล', icon: 'fas fa-cogs' },
      { id: 'ghosts', name: 'วิญญาณ', icon: 'fas fa-ghost' },
      { id: 'vehicle', name: 'พาหนะ', icon: 'fas fa-motorcycle' },
      { id: 'setBonuses', name: 'โบนัสจากเซ็ต', icon: 'fas fa-star' },
      
    ]);

    const allItemTypes = [
      'หมวก', 'แว่นตา', 'เสื้อ', 'เสื้อคลุม', 'เกราะ', 'ปลอกแขน', 'ถุงมือ', 'กางเกง', 'สนับแข้ง',
      'รองเท้า', 'เข็มขัด', 'สร้อยคอ', 'แหวน', 'อาวุธ', 'อาวุธสำรอง', 'ตะกรุด', 'ผ้ายันต์', 'สายสิญจน์',
    ];

    const mechanicalNamesSkill1 = [
      'ดวงตาจักรกล', 'สมองจักรกล', 'หัวใจจักรกล', 'ซี่โครงจักรกล', 'แขนจักรกล', 'หมัดจักรกล', 'ขาจักรกล',
    ];

    const mechanicalNamesSkill2 = [
      'ลำตัวจักรกล', 'สะโพกจักรกล', 'ตาหลังจักรกล', 'กรามจักรกล', 'ไหล่จักรกล', 'จมูกจักรกล', 'หูจักรกล',
    ];

    const mechanicalNamesSkill3 = [
      'ปีกจักรกล', 'หนวดจักรกล', 'เขาจักรกล',
    ];

    const ghostSubtypes = ref([
      'วิญญาณเฝ้าห้อง',
      'วิญญาณค้นหาผู้เล่น',
      'วิญญาณผู้พิทักษ์'
    ]);

    const typeIcons = {
      'หมวก': 'fas fa-ban', 'แว่นตา': 'fas fa-ban', 'เสื้อ': 'fas fa-ban', 'เสื้อคลุม': 'fas fa-ban',
      'เกราะ': 'fas fa-ban', 'ปลอกแขน': 'fas fa-ban', 'ถุงมือ': 'fas fa-ban', 'กางเกง': 'fas fa-ban',
      'สนับแข้ง': 'fas fa-ban', 'รองเท้า': 'fas fa-ban', 'เข็มขัด': 'fas fa-ban', 'สร้อยคอ': 'fas fa-ban',
      'แหวน': 'fas fa-ban', 'อาวุธ': 'fas fa-ban', 'อาวุธสำรอง': 'fas fa-ban', 'ตะกรุด': 'fas fa-ban',
      'ผ้ายันต์': 'fas fa-ban', 'สายสิญจน์': 'fas fa-ban',
    };

    const PUNISHMENT_PROTECTED_TYPES = ['ตะกรุด', 'ผ้ายันต์', 'สายสิญจน์', 'สร้อยคอ'];
    const isPunishmentProtected = (type) => PUNISHMENT_PROTECTED_TYPES.includes(type);

    const statOrder = ['stamina', 'str', 'int', 'agi', 'luck', 'maxHp', 'atk', 'evasion', 'critical', 'defense'];

    const setConfigs = {
      'เซ็ตตำรวจ': {
        items: [
          'หมวกตำรวจ', 'เสื้อตำรวจ', 'เสื้อคลุมตำรวจ', 'กางเกงตำรวจ',
          'รองเท้าหนัง', 'เข็มขัดตำรวจ', 'กระบองตำรวจ'
        ],
        bonuses: {
          4: { critical: 2, evasion: 2, atk: 10 },
          7: { critical: 5, evasion: 5, atk: 30 }
        }
      },
      'เซ็ตทหาร': {
        items: [
          'หมวกทหาร', 'เสื้อกองทัพบก', 'เสื้อคลุมทหาร', 'ถุงมือทหาร',
          'กางเกงทหาร', 'รองเท้าคอมแบท', 'เข็มขัดทหาร', 'แท่งเหล็กปลายมีด'
        ],
        bonuses: {
          4: { critical: 4, evasion: 4, atk: 20 },
          8: { critical: 10, evasion: 10, atk: 50 }
        }
      },
      'เซ็ตนักดับเพลิง': {
        items: [
          'หมวกนิรภัย', 'เสื้อนักดับเพลิง', 'เสื้อคลุมนักดับเพลิง', 'ถุงมือนักดับเพลิง',
          'กางเกงนักดับเพลิง', 'รองเท้านักดับเพลิง', 'เข็มขัดนักดับเพลิง', 'เลื่อยไฟฟ้า'
        ],
        bonuses: {
          4: { maxHp: 1000, critical: 4, defense: 20 },
          8: { maxHp: 2500, critical: 10, defense: 50, atk: 10 }
        }
      },
      'เซ็ตตัวตลก': {
        items: ['ถุงมือตัวตลก', 'เสื้อตัวตลก', 'กางเกงตัวตลก', 'หมวกตัวตลก', 'รองเท้าตัวตลก'],
        bonuses: {
          2: { atk: 15, critical: 10 },
          5: { atk: 40, critical: 20 }
        }
      },
      'เซ็ตเทควันโด้': {
        items: ['เสื้อเทควันโด', 'เสื้อคลุมเทควันโด', 'กางเกงเทควันโด'],
        bonuses: {
          3: { evasion: 20, atk: 30 }
        }
      },
      'เซ็ตนักรบไทย': {
        items: ['เกราะนักรบไทย', 'ปลอกแขนนักรบไทย', 'สนับแข้งนักรบไทย', 'หมวกนักรบไทย', 'หมวกอัศวิน', 'ดาบ'],
        bonuses: {
          3: { critical: 3, evasion: 3, atk: 20 },
          4: { critical: 6, evasion: 6, atk: 30 },
          5: { critical: 10, evasion: 10, atk: 40 }
        }
      }
    };

    const equippedByType = computed(() => {
      const map = {};
      equippedItems.value.forEach(item => {
        if (item && item.type) {
          map[item.type] = item;
        }
      });
      return map;
    });

    const mechanicalByName = computed(() => {
      const map = {};
      equippedMechanicalOrgans.value.forEach(item => {
        const key = item.slot || item.name;
        if (item && key) {
          map[key] = item;
        }
      });
      return map;
    });

    const setProgress = computed(() => {
      const progress = [];
      const equippedNames = equippedItems.value.map(item => gameItemsData[item.templateId]?.name || item.name).filter(Boolean);
      for (const [setName, config] of Object.entries(setConfigs)) {
        const equippedInSet = config.items.filter(item => equippedNames.includes(item));
        const equippedCount = equippedInSet.length;
        if (equippedCount === 0) continue;
        const totalItems = config.items.length;
        const bonusKeys = Object.keys(config.bonuses).map(Number).sort((a, b) => a - b);
        let currentBonus = null;
        const nextBonuses = [];
        for (let i = bonusKeys.length - 1; i >= 0; i--) {
          const required = bonusKeys[i];
          if (equippedCount >= required) {
            currentBonus = config.bonuses[required];
            break;
          }
        }
        for (const required of bonusKeys) {
          if (equippedCount < required) {
            nextBonuses.push({ required, bonus: config.bonuses[required] });
          }
        }
        const missingItems = config.items.filter(item => !equippedNames.includes(item));
        progress.push({
          setName,
          equippedCount,
          totalItems,
          currentBonus,
          nextBonuses,
          missingItems
        });
      }
      return progress;
    });

    const isGhostSubtypeEquipped = computed(() => {
      return (subtype) => {
        if (!Array.isArray(equippedGhosts.value)) return false;
        return equippedGhosts.value.some(g => g && g.subtype === subtype);
      };
    });

    const statMeta = {
      stamina: { label: 'Stamina', icon: 'fas fa-heart' },
      str: { label: 'STR', icon: 'fas fa-dumbbell' },
      int: { label: 'INT', icon: 'fas fa-brain' },
      agi: { label: 'AGI', icon: 'fas fa-running' },
      luck: { label: 'Luck', icon: 'fas fa-clover' },
      maxHp: { label: 'Max HP', icon: 'fas fa-heartbeat' },
      atk: { label: 'ATK', icon: 'fas fa-hand-fist' },
      evasion: { label: 'Evasion', icon: 'fas fa-person-running' },
      critical: { label: 'Critical', icon: 'fas fa-burst' },
      defense: { label: 'Defense', icon: 'fas fa-shield-alt' },
    };

    const getStat = (obj, key) => {
      if (!obj || typeof obj !== 'object') return 0;
      const val = obj[key];
      return typeof val === 'number' ? val : 0;
    };

    const formatBonus = (num) => {
      if (!num) return '—';
      return num > 0 ? `+${num}` : `${num}`;
    };

    const equippedNamesSet = computed(() => {
      const set = new Set();
      equippedItems.value.forEach(it => { if (it?.name) set.add(it.name); });
      return set;
    });

    const handleImageError = (event) => {
      event.target.src = 'default-item.png';
    };

    const inventoryStore = useInventoryStore();

    const updateInventoryItems = () => {
      const bagData = inventoryStore.bagData;
      if (bagData && bagData.items) {
        const itemsObj = bagData.items || {};
        const mappedItems = Object.entries(itemsObj).map(([instanceId, itemData]) => {
          const tpl = gameItemsData[itemData.templateId] || {};
          return {
            id: instanceId,
            instanceId,
            name: tpl.name || itemData.templateId,
            templateId: itemData.templateId,
            quantity: itemData.states?.quantity || 1,
            ...tpl,
            ...itemData.states,
          };
        });
        inventoryItems.value = mappedItems;
      } else {
        inventoryItems.value = [];
      }
    };

    watch(() => inventoryStore.bagData, updateInventoryItems, { immediate: true });

    const fetchEquippedItems = async () => {
      if (!user.value) {
        error.value = 'กรุณาล็อกอินเพื่อดูไอเทมที่สวมใส่';
        loading.value = false;
        return;
      }
    };

    watch(() => playerStore.characterPrivateData, async (charPrivateData) => {
      if (!charPrivateData) return;
      const equippedItemIds = charPrivateData?.equippedItemIds || {};
      const vehicleInstanceId = charPrivateData?.vehicle || null;
      const mechanicalItems = charPrivateData?.mechanicalItems || {};
      const ghostItemIds = charPrivateData?.ghostItemIds || [];
      const ghostNames = charPrivateData?.ghostNames || [];
      const ghostTypes = charPrivateData?.ghostTypes || [];
      itemStats.value = charPrivateData?.itemStats || { stamina: 0, str: 0, int: 0, agi: 0, luck: 0, maxHp: 0, atk: 0, evasion: 0, critical: 0, defense: 0 };
      ghostStats.value = charPrivateData?.ghostStats || { stamina: 0, str: 0, int: 0, agi: 0, luck: 0, maxHp: 0, atk: 0, evasion: 0, critical: 0, defense: 0 };
      setBonuses.value = charPrivateData?.setBonuses || {};
      defensePoints.value = charPrivateData?.defensePoints || 0;
      equippedItemTypes.value = charPrivateData?.equippedItemTypes || [];

      const bagData = inventoryStore.bagData;
      const bagItems = bagData?.items || {};

      if (Object.keys(equippedItemIds).length) {
        try {
          const equippedItemsData = charPrivateData.equippedItems || {};
          const fetchedItems = Object.entries(equippedItemIds).map(([type, instanceId]) => {
            const equippedItem = equippedItemsData[instanceId];
            if (!equippedItem) return null;
            const template = gameItemsData[equippedItem.templateId] || {};
            return {
              id: instanceId,
              instanceId,
              type,
              templateId: equippedItem.templateId,
              quantity: equippedItem.quantity || 1,
              states: equippedItem.states || {},
              level: equippedItem.states?.level,
              upgradeStats: equippedItem.states?.upgradeStats || {},
              rarity: equippedItem.states?.rarity ?? template.rarity ?? 1,
              ...template,
              transfer: equippedItem.states?.transfer ?? template.transfer ?? false,
            };
          }).filter(item => item !== null);
          equippedItems.value = fetchedItems;
        } catch (err) {
          error.value = err.message || 'เกิดข้อผิดพลาดในการโหลดไอเทม';
          equippedItems.value = [];
        }
      } else {
        equippedItems.value = [];
      }

      if (vehicleInstanceId) {
        try {
          let bagItem = bagItems[vehicleInstanceId];
          if (!bagItem && charPrivateData.vehicleData) {
            bagItem = charPrivateData.vehicleData;
          }
          if (bagItem) {
            const states = bagItem.states || {}

            const template = gameItemsData[bagItem.templateId] || {};
            equippedVehicle.value = {
  ...template,
  id: vehicleInstanceId,
  instanceId: vehicleInstanceId,
  templateId: bagItem.templateId,
  quantity: bagItem.quantity || 1,
  states,
  level: states.level,
  fuelLevel: states.fuelLevel ?? bagItem.fuelLevel ?? template.fuelLevel ?? 0,
  maxFuelLevel: states.maxFuelLevel ?? bagItem.maxFuelLevel ?? template.maxFuelLevel ?? 100,
  transfer: states.transfer ?? template.transfer ?? false
}
          } else {
            equippedVehicle.value = null;
          }
        } catch (err) {
          error.value = err.message || 'เกิดข้อผิดพลาดในการโหลดพาหนะ';
          equippedVehicle.value = null;
        }
      } else {
        equippedVehicle.value = null;
      }

      if (Object.keys(mechanicalItems).length) {
        try {
          const fetchedMechanical = Object.entries(mechanicalItems).map(([instanceId, mechData]) => {
            const template = gameItemsData[mechData.templateId] || {};
            return {
              id: instanceId,
              instanceId,
              templateId: mechData.templateId,
              quantity: mechData.quantity || 1,
              states: mechData.states || {},
              level: mechData.states?.level,
              slot: mechData.slot || mechData.name,
              ...template,
              rarity: mechData.states?.rarity ?? template.rarity ?? 1,
              rarityColors: mechData.states?.rarityColors ?? template.rarityColors ?? { bgColor: 'bg-gray-700', textColor: 'text-gray-500', borderColor: 'border-gray-600' },
              upgradeCount: mechData.states?.upgradeCount ?? 0,
              statPoints: mechData.states?.statPoints ?? 0,
              upgradeStats: mechData.states?.upgradeStats ?? {},
              transfer: mechData.states?.transfer ?? template.transfer ?? false,
            };
          }).filter(item => item !== null);
          equippedMechanicalOrgans.value = fetchedMechanical;
        } catch (err) {
          error.value = err.message || 'เกิดข้อผิดพลาดในการโหลดอวัยวะจักรกล';
          equippedMechanicalOrgans.value = [];
        }
      } else {
        equippedMechanicalOrgans.value = [];
      }

      const ghostItems = charPrivateData?.ghostItems || {};
      if (Object.keys(ghostItems).length) {
        try {
          const fetchedGhosts = Object.entries(ghostItems).map(([instanceId, ghostData]) => {
            const template = gameItemsData[ghostData.templateId] || {};
            return {
              id: instanceId,
              instanceId,
              templateId: ghostData.templateId,
              states: ghostData.states || {},
              ...template,
              rarity: ghostData.states?.rarity ?? template.rarity ?? 1,
              rarityColors: ghostData.states?.rarityColors ?? template.rarityColors ?? { bgColor: 'bg-gray-700', textColor: 'text-gray-500', borderColor: 'border-gray-600' },
              subtype: template.subtype || ghostData.states?.subtype || 'วิญญาณ',
              cooldownUntil: ghostData.states?.cooldownUntil || null,
              transfer: ghostData.states?.transfer ?? template.transfer ?? false,
            };
          });
          equippedGhosts.value = fetchedGhosts;
        } catch (err) {
          error.value = err.message || 'เกิดข้อผิดพลาดในการโหลดวิญญาณ';
          equippedGhosts.value = [];
        }
      } else if (ghostItemIds.length) {
        try {
          const ghostPromises = ghostItemIds.map(async (itemId, index) => {
            if (!itemId) return null;
            const ghostItems = charPrivateData.ghostItems || {};
            let ghostData = ghostItems[itemId];
            if (!ghostData) {
              ghostData = bagItems[itemId];
            }
            if (ghostData) {
              const tpl = gameItemsData[ghostData.templateId] || {};
              return {
                id: itemId,
                instanceId: itemId,
                ...tpl,
                ...ghostData.states,
                subtype: ghostTypes[index] || ghostData.states?.subtype || tpl.subtype || 'วิญญาณ',
              };
            }
            return null;
          });
          const fetchedGhosts = await Promise.all(ghostPromises);
          equippedGhosts.value = fetchedGhosts.filter(item => item !== null);
        } catch (err) {
          error.value = err.message || 'เกิดข้อผิดพลาดในการโหลดวิญญาณ';
          equippedGhosts.value = [];
        }
      } else {
        equippedGhosts.value = [];
      }

      loading.value = false;
    }, { immediate: true });

    const selectItem = (item, source) => {
      if (item) {
        selectedItem.value = item;
        selectedItemSource.value = source;
        confirmUnequip.value = false;
        confirmUnequipVehicle.value = false;
        confirmUnequipMechanical.value = false;
        confirmUnsummon.value = false;
        showDetailModal.value = false;
        showVehicleFuelModal.value = false;
      }
    };

    const unequipItem = async () => {
      if (!user.value || !selectedItem.value?.id) {
        error.value = 'ไม่สามารถถอนไอเทมได้: ไม่พบผู้ใช้หรือไอเทม';
        return;
      }
      isUnequipping.value = true;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const result = await gameActions({
          action: 'unequipItem',
          instanceId: selectedItem.value.id,
          slot: selectedItem.value.type,
        });
        if (result.data.success) {
          selectedItem.value = null;
          selectedItemSource.value = null;
          confirmUnequip.value = false;
          showDetailModal.value = false;
        } else {
          error.value = result.data.message || 'การถอนไอเทมล้มเหลว';
        }
      } catch (err) {
        error.value = err.message || 'เกิดข้อผิดพลาดในการถอนไอเทม';
      } finally {
        isUnequipping.value = false;
      }
    };

    const unequipVehicle = async () => {
      if (!user.value || !selectedItem.value?.id) {
        error.value = 'ไม่สามารถถอนพาหนะได้: ไม่พบผู้ใช้หรือพาหนะ';
        return;
      }
      isUnequipping.value = true;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const result = await gameActions({ action: 'vehicleAction', subAction: 'unequip', instanceId: selectedItem.value.id });
        if (result.data.success) {
          selectedItem.value = null;
          selectedItemSource.value = null;
          confirmUnequipVehicle.value = false;
          showDetailModal.value = false;
          showVehicleFuelModal.value = false;
        } else {
          error.value = result.data.message || 'การถอนพาหนะล้มเหลว';
        }
      } catch (err) {
        error.value = err.message || 'เกิดข้อผิดพลาดในการถอนพาหนะ';
      } finally {
        isUnequipping.value = false;
      }
    };

    const unequipMechanicalOrgan = async () => {
      if (!user.value || !selectedItem.value?.instanceId) {
        error.value = 'ไม่สามารถถอนอวัยวะจักรกลได้: ไม่พบผู้ใช้หรืออวัยวะจักรกล';
        return;
      }
      isUnequipping.value = true;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const result = await gameActions({ 
          action: 'mechanicalOrganAction', 
          subAction: 'unequip', 
          instanceId: selectedItem.value.instanceId 
        });
        if (result.data.success) {
          selectedItem.value = null;
          selectedItemSource.value = null;
          confirmUnequipMechanical.value = false;
          showDetailModal.value = false;
        } else {
          error.value = result.data.message || 'การถอนอวัยวะจักรกลล้มเหลว';
        }
      } catch (err) {
        error.value = err.message || 'เกิดข้อผิดพลาดในการถอนอวัยวะจักรกล';
      } finally {
        isUnequipping.value = false;
      }
    };

    const unsummonGhost = async () => {
      if (!user.value || !selectedItem.value?.id) {
        error.value = 'ไม่สามารถถอนการอัญเชิญวิญญาณได้: ไม่พบผู้ใช้หรือวิญญาณ';
        return;
      }
      isUnequipping.value = true;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const result = await gameActions({ action: 'ghostCommandAction', subAction: 'unsummon', instanceId: selectedItem.value.id });
        if (result.data.success) {
          selectedItem.value = null;
          selectedItemSource.value = null;
          confirmUnsummon.value = false;
          showDetailModal.value = false;
        } else {
          error.value = result.data.message || 'การถอนการอัญเชิญวิญญาณล้มเหลว';
        }
      } catch (err) {
        error.value = err.message || 'เกิดข้อผิดพลาดในการถอนการอัญเชิญวิญญาณ';
      } finally {
        isUnequipping.value = false;
      }
    };

    const handleVehicleUpdated = (updatedItem) => {
      if (updatedItem && updatedItem.id === equippedVehicle.value?.id) {
        equippedVehicle.value = { ...equippedVehicle.value, ...updatedItem };
        selectedItem.value = equippedVehicle.value;
      }
      showVehicleFuelModal.value = false;
      error.value = null;
    };

    const openDetailModal = (item) => {
      selectedItem.value = item;
      showDetailModal.value = true;
    };

    const updateIsMobile = () => {
      isMobile.value = window.innerWidth < 768;
    };

    const getVehicleFuel = (vehicle) => {
  return vehicle?.states?.fuelLevel ?? vehicle?.fuelLevel ?? 0
}

const getVehicleMaxFuel = (vehicle) => {
  return vehicle?.states?.maxFuelLevel ?? vehicle?.maxFuelLevel ?? 100
}

    onMounted(async () => {
      await fetchEquippedItems();
      updateInventoryItems();
      window.addEventListener('resize', updateIsMobile);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', updateIsMobile);
    });

    return {
        getVehicleFuel,
  getVehicleMaxFuel,

      activeTab,
      tabs,
      equippedItems,
      equippedVehicle,
      equippedMechanicalOrgans,
      equippedGhosts,
      itemStats,
      ghostStats,
      setBonuses,
      setProgress,
      defensePoints,
      loading,
      error,
      selectedItem,
      selectedItemSource,
      confirmUnequip,
      confirmUnequipVehicle,
      confirmUnequipMechanical,
      confirmUnsummon,
      isUnequipping,
      isMobile,
      allItemTypes,
      equippedByType,
      mechanicalNamesSkill1,
      mechanicalNamesSkill2,
      mechanicalNamesSkill3,
      mechanicalByName,
      ghostSubtypes,
      isGhostSubtypeEquipped,
      typeIcons,
      isPunishmentProtected,
      statOrder,
      statMeta,
      getStat,
      formatBonus,
      equippedNamesSet,
      selectItem,
      unequipItem,
      unequipVehicle,
      unequipMechanicalOrgan,
      unsummonGhost,
      handleImageError,
      showDetailModal,
      showVehicleFuelModal,
      inventoryItems,
      handleVehicleUpdated,
      openDetailModal,
      setConfigs
    };
  },
};
</script>

<style scoped>
.max-h-96 { max-height: 24rem; }
.overflow-y-auto { overflow-y: auto; }
/* Animations */
.animate-spin { animation: spin 1s linear infinite; }
.animate-spin-pulse { animation: spin-pulse 1.2s ease-in-out infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes spin-pulse {
  0% { transform: rotate(0deg) scale(1); }
  50% { transform: rotate(180deg) scale(1.2); }
  100% { transform: rotate(360deg) scale(1); }
}
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.3s ease-out; }
@keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.3s ease-out; }
/* Scrollbar */
.overflow-y-auto::-webkit-scrollbar { width: 5px; }
.overflow-y-auto::-webkit-scrollbar-track { background: #374151; border-radius: 10px; }
.overflow-y-auto::-webkit-scrollbar-thumb { background: #4b5563; border-radius: 10px; }
.overflow-y-auto::-webkit-scrollbar-thumb:hover { background: #6b7280; }
/* Buttons */
button:disabled { opacity: 0.6; cursor: not-allowed; }
button:not(:disabled):active { transform: scale(0.98); }
/* Chips used elsewhere */
.chip {
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.7rem;
  line-height: 0.95rem;
  border: 1px solid #374151; /* gray-700 */
}
.chip-green {
  background: rgba(6, 78, 59, 0.3); /* green-900 @ 30% */
  color: #bbf7d0; /* green-200 */
  border-color: #065f46; /* green-700 */
}
.chip-amber {
  background: rgba(120, 53, 15, 0.2); /* amber/yellow-900 @ 20% */
  color: #fde68a; /* yellow-200 */
  border-color: #92400e; /* amber/yellow-700 */
}
.stat-pill {
  display: flex; align-items: center; justify-content: space-between; gap: 0.5rem;
  padding: 0.25rem 0.5rem; border-radius: 0.375rem;
  border: 1px solid #374151; /* gray-700 */
  background: #111827; /* gray-900 */
}
.stat-pill--next { background: #1f2937; /* gray-800 */ }
/* text-2xs for Tailwind 2.x */
.text-2xs { font-size: 0.7rem; line-height: 0.95rem; }
</style>
